import React, { useEffect, useState, useRef } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  ReactFlowProvider,
  useReactFlow,
  Handle,
  Position
} from "@xyflow/react";
import dagre from "dagre";
import { useNavigate } from "react-router-dom";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import "@xyflow/react/dist/style.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const nodeWidth = 180;
const nodeHeight = 140;

// 🔹 Dummy MAC adresi üretici (gateway ID'ye göre deterministik)
const generateMac = (id) => {
  const seed = (id * 0x1f3a91 + 0xab42cd) >>> 0;
  const hex = seed.toString(16).padStart(8, "0").toUpperCase();
  return `A4:CF:${hex.slice(0, 2)}:${hex.slice(2, 4)}:${hex.slice(4, 6)}:${hex.slice(6, 8)}`;
};

// 🔹 Dagre layout hesaplama
const getLayoutedElements = (nodes, edges, direction = "TB") => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    ranksep: 90,
    nodesep: 70,
    marginx: 30,
    marginy: 30,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const pos = dagreGraph.node(node.id);
    return {
      ...node,
      position: { x: pos.x - nodeWidth / 2, y: pos.y - nodeHeight / 2 },
      targetPosition: "top",
      sourcePosition: "bottom",
    };
  });

  return { nodes: layoutedNodes, edges };
};

// 🔹 Junction bazlı mimic bağlantı üretimi
const buildMimicEdges = (rawNodes, rawEdges) => {
  if (!Array.isArray(rawNodes) || !Array.isArray(rawEdges)) return [];

  const safeNodes = rawNodes.filter((n) => n && n.id);
  const safeEdges = rawEdges.filter((e) => e && e.source && e.target);

  const junctions = safeNodes.filter((n) => n.id.startsWith("J_"));
  const resultEdges = [];

  junctions.forEach((j) => {
    const inEdges = safeEdges.filter((e) => e.target === j.id);
    const outEdges = safeEdges.filter((e) => e.source === j.id);

    inEdges.forEach((inE) => {
      outEdges.forEach((outE) => {
        if (!inE.source.startsWith("J_") && !outE.target.startsWith("J_")) {
          resultEdges.push({
            id: `${inE.source}-${outE.target}`,
            source: inE.source,
            target: outE.target,
            label: outE.label || "",
          });
        }
      });
    });
  });

  safeEdges.forEach((e) => {
    if (!e.source.startsWith("J_") && !e.target.startsWith("J_")) {
      resultEdges.push(e);
    }
  });

  const unique = [];
  const seen = new Set();
  for (const e of resultEdges) {
    const key = `${e.source}-${e.target}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(e);
    }
  }

  return unique;
};

// 🔹 Cihaz node bileşeni
const CustomNode = ({ data }) => {
  const { type, label } = data;

  let iconSrc = "/assets/analyzer.png";
  if (type?.includes("Reaktif") || type === "mimic.reactiveRelay") {
    iconSrc = "/assets/reactive.png";
  } else if (type?.includes("Sanal") || type === "mimic.virtualDevice") {
    iconSrc = "/icons/virtual.png";
  } else if (type?.includes("Multimetre") || type === "mimic.multimeter" || type?.includes("PMA")) {
    iconSrc = "/assets/multimeter.png";
  }

  return (
    <div className="flex flex-col items-center text-center select-none relative">
      <img
        src={iconSrc}
        alt={type}
        style={{ width: 100, height: 100, objectFit: "contain", marginBottom: 6 }}
      />
      <div
        style={{
          fontSize: 25,
          fontWeight: 600,
          color:
            type?.includes("Reaktif") || type === "mimic.reactiveRelay"
              ? "#2563eb"
              : type?.includes("Sanal") || type === "mimic.virtualDevice"
                ? "#9333ea"
                : "#16a34a",
        }}
      >
        {label}
      </div>
      <Handle type="target" position={Position.Top} style={{ opacity: 0, width: 6, height: 6 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, width: 6, height: 6 }} />
    </div>
  );
};

// 🔹 Gateway node bileşeni
const GatewayNode = ({ data }) => {
  const { label } = data;
  return (
    <div className="flex flex-col items-center text-center select-none relative">
      <img
        src="/assets/gateway.png"
        alt="gateway"
        style={{ width: 90, height: 90, objectFit: "contain", marginBottom: 6 }}
      />
      <div style={{ fontSize: 22, fontWeight: 700, color: "#d97706" }}>
        {label}
      </div>
      <Handle type="target" position={Position.Top} style={{ opacity: 0, width: 6, height: 6 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, width: 6, height: 6 }} />
    </div>
  );
};


function MimicDiagramInner() {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const reactFlowRef = useRef(null);
  const navigate = useNavigate();
  const { fitView } = useReactFlow();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [noTopology, setNoTopology] = useState(false);

  const handleNodeClick = (_, node) => {
    if (!node?.id) return;
    if (node.id.startsWith("GW_")) {
      setSelectedGateway(node.data);
    } else {
      setSelectedDevice(node);
    }
  };

  const goToDashboard = () => {
    if (!selectedDevice) return;

    if (selectedDevice.id.startsWith("V_")) {
      const virtualId = selectedDevice.id.replace("V_", "");
      localStorage.setItem("selectedDevice", JSON.stringify({
        mode: "virtual", virtualId,
        name: selectedDevice.data?.label || "Sanal Cihaz",
        type: "virtual",
      }));
      localStorage.setItem("deviceType", "virtual");
      navigate(`/dashboard/${virtualId}/virtual/voltages`);
    } else {
      const deviceId = parseInt(selectedDevice.id);
      const type = (selectedDevice.data?.type?.includes("Reaktif") || selectedDevice.data?.type === "mimic.reactiveRelay")
        ? "reactive"
        : "analyzer";
      localStorage.setItem("selectedDevice", JSON.stringify({
        mode: "physical", deviceId,
        name: selectedDevice.data?.label || "Cihaz",
        type,
      }));
      localStorage.setItem("deviceType", type);
      navigate(`/devices/${deviceId}/${type}/real-time`);
    }
  };

  useEffect(() => {
    async function loadHierarchy() {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(`${API_BASE}/hierarchy/load`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        console.log(json);
        if (!json.success) {
          setLoading(false);
          setNoTopology(true);
          return;
        }

        const rawNodes = json.nodes;
        const rawEdges = json.edges;
        const mimicEdges = buildMimicEdges(rawNodes, rawEdges);
        const filteredNodes = rawNodes.filter((n) => !n.id.startsWith("J_"));

        const res2 = await fetch(`${API_BASE}/gateways/all-with-coords`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await res2.json();
        const gateways = Array.isArray(result)
          ? result
          : result.gateways || result.data || [];
        if (!Array.isArray(gateways)) return;

        const allDevices = gateways.flatMap((gw) => gw.devices || []);
        const deviceMap = {};
        allDevices.forEach((d) => (deviceMap[d.id] = d));

        // 🔹 Cihaz → Gateway eşleme tablosu
        const deviceToGatewayMap = {};
        gateways.forEach((gw) => {
          (gw.devices || []).forEach((d) => {
            deviceToGatewayMap[d.id] = gw;
          });
        });

        // 3️⃣ Node'ları zenginleştir
        const nodeToGateway = {};
        const styledNodes = filteredNodes.map((n) => {
          const isVirtual = n.id.startsWith("V_");
          const deviceId =
            n.device_id ||
            n.virtual_id ||
            n.data?.device_id ||
            n.data?.deviceId ||
            parseInt(n.id);

          const device = deviceMap[deviceId] || {};

          if (!isVirtual) {
            const gw = deviceToGatewayMap[deviceId];
            if (gw) nodeToGateway[n.id] = gw;
          }

          return {
            id: n.id,
            data: {
              label: n.data.label || n.data.name || device.name || "Cihaz",
              type:
                (device.type &&
                  (device.type.toUpperCase().startsWith("PRC")
                    ? "mimic.reactiveRelay"
                    : "mimic.analyzer")) ||
                n.data.type ||
                (isVirtual ? "mimic.virtualDevice" : "mimic.unknown"),
              model: device.model || n.data.model || "-",
              latitude: device.latitude ?? n.latitude ?? null,
              longitude: device.longitude ?? n.longitude ?? null,
              location:
                device.latitude && device.longitude
                  ? `${device.latitude.toFixed(4)}, ${device.longitude.toFixed(4)}`
                  : null,
            },
            type: "custom",
            position: { x: 0, y: 0 },
          };
        });

        // 🔹 Edge stilleri
        const styledEdges = mimicEdges.map((e) => ({
          id: `${e.source}-${e.target}`,
          source: e.source,
          target: e.target,
          type: "smoothstep",
          animated: e.label === "parallel",
          style: {
            stroke: e.label === "parallel" ? "#2563eb" : "#16a34a",
            strokeDasharray: e.label === "parallel" ? "6 4" : "0",
            strokeWidth: 2.5,
          },
        }));

        // 🔹 Gateway node'ları ve bağlantı edge'leri
        const usedGatewayMap = new Map();
        const gatewayEdges = [];
        Object.entries(nodeToGateway).forEach(([nodeId, gw]) => {
          const gwNodeId = `GW_${gw.id}`;
          if (!usedGatewayMap.has(gw.id)) usedGatewayMap.set(gw.id, gw);
          gatewayEdges.push({
            id: `${gwNodeId}->${nodeId}`,
            source: gwNodeId,
            target: nodeId,
            type: "smoothstep",
            animated: false,
            style: { stroke: "#d97706", strokeWidth: 2.5, strokeDasharray: "6 3" },
          });
        });

        const gatewayNodes = [...usedGatewayMap.values()].map((gw) => {
          const devices = gw.devices || [];
          const analyzerCount = devices.filter((d) =>
            ["PAD-01", "PAD-02", "PAD-03"].includes(d.type)
          ).length;
          const reactiveCount = devices.filter((d) =>
            ["PRC-15H+TCR", "Grup Arge Klasik Röle"].includes(d.type)
          ).length;
          const multimeterCount = devices.filter((d) => d.type === "PMA-03").length;

          return {
            id: `GW_${gw.id}`,
            data: {
              label: gw.name || `Gateway ${gw.id}`,
              type: "gateway",
              mac: generateMac(gw.id),
              analyzerCount,
              reactiveCount,
              multimeterCount,
              totalDevices: devices.length,
              last_comm: gw.last_comm || null,
              latitude: gw.latitude || null,
              longitude: gw.longitude || null,
            },
            type: "gateway",
            position: { x: 0, y: 0 },
          };
        });

        const allNodes = [...gatewayNodes, ...styledNodes];
        const allEdges = [...gatewayEdges, ...styledEdges];

        if (allNodes.length === 0) {
          setNodes([]);
          setEdges([]);
          setNoTopology(true);
          setLoading(false);
          return;
        }

        const layouted = getLayoutedElements(allNodes, allEdges, "TB");
        setNodes(layouted.nodes);
        setEdges(layouted.edges);

        setTimeout(() => fitView({ padding: 0.15, duration: 600 }), 300);
        setLoading(false);
      } catch (err) {
        console.error("❌ mimic diagram error:", err);
        setLoading(false);
        setNoTopology(true);
      }
    }

    loadHierarchy();
  }, []);

  // 🔹 Gateway last_comm formatla
  const formatLastComm = (last_comm) => {
    if (!last_comm) return null;
    const d = new Date(last_comm);
    const isLate = (new Date() - d) > 30 * 60 * 1000;
    return { text: d.toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }), isLate };
  };

  return (
    <>
      <div className="w-full h-[90vh] bg-gray-100 rounded-lg shadow-inner border flex items-center justify-center">
        {loading ? (
          <p className="text-gray-500 text-sm">{t("mimic.topologyLoading")}</p>
        ) : noTopology ? (
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-700 mb-2">{t("mimic.noTopologyTitle")}</p>
            <p className="text-sm text-gray-500">{t("mimic.noTopologyDesc")}</p>
          </div>
        ) : (
          <ReactFlow
            ref={reactFlowRef}
            nodes={nodes}
            edges={edges}
            fitView
            onNodeClick={handleNodeClick}
            proOptions={{ hideAttribution: true }}
            nodeTypes={{ custom: CustomNode, gateway: GatewayNode }}
            onErrorCapture={(e) => e.stopPropagation()}
            style={{ width: "100%", height: "100%" }}
          >
            <Controls />
            <Background color="#d1d5db" gap={16} />
          </ReactFlow>
        )}
      </div>

      {/* 🔹 Cihaz Detay Dialog */}
      <Dialog
        open={!!selectedDevice}
        onClose={() => setSelectedDevice(null)}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      >
        <Dialog.Panel className="relative bg-gray-900/80 backdrop-blur-xl border border-gray-700/50 rounded-2xl shadow-2xl p-6 w-[380px] text-gray-100">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl shadow-lg bg-gray-800/40">
              <img
                src={
                  selectedDevice?.data?.type?.includes("Reaktif") || selectedDevice?.data?.type === "mimic.reactiveRelay"
                    ? "/assets/reactive.png"
                    : selectedDevice?.data?.type?.includes("Sanal") || selectedDevice?.data?.type === "mimic.virtualDevice"
                      ? "/icons/virtual.png"
                      : selectedDevice?.data?.type?.includes("Multimetre") ||
                        selectedDevice?.data?.type === "mimic.multimeter" ||
                        selectedDevice?.data?.model === "PMA-03"
                        ? "/assets/multimeter.png"
                        : "/assets/analyzer.png"
                }
                alt="icon"
                className="w-9 h-9 object-contain"
              />
            </div>
            <div>
              <Dialog.Title className="text-lg font-semibold tracking-wide text-white">
                {selectedDevice?.data?.label || t("mimic.deviceName")}
              </Dialog.Title>
              <p className="text-xs text-gray-400">{t("mimic.deviceInfo")}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-6">
            <div className="text-gray-400 font-medium">{t("common.type")}</div>
            <div className="text-gray-100">{t(selectedDevice?.data?.type || "mimic.unknown")}</div>
            <div className="text-gray-400 font-medium">{t("common.model")}</div>
            <div className="text-gray-100">{selectedDevice?.data?.model || "-"}</div>
            <div className="text-gray-400 font-medium">{t("common.location")}</div>
            <div className="text-gray-100">{selectedDevice?.data?.location || t("common.undefined")}</div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={goToDashboard}
              className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-cyan-500 text-white py-2.5 rounded-xl font-medium shadow-md transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-6H6" />
              </svg>
              {t("mimic.goToDashboard")}
            </button>
            <button
              onClick={() => setSelectedDevice(null)}
              className="flex-1 bg-gray-700 text-gray-200 py-2.5 rounded-xl font-medium hover:bg-gray-600 transition-all"
            >
              {t("common.close")}
            </button>
          </div>
        </Dialog.Panel>
      </Dialog>

      {/* 🔹 Gateway Detay Dialog */}
      <Dialog
        open={!!selectedGateway}
        onClose={() => setSelectedGateway(null)}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      >
        <Dialog.Panel className="relative bg-gray-900/80 backdrop-blur-xl border border-amber-700/40 rounded-2xl shadow-2xl p-6 w-[400px] text-gray-100">
          {/* Başlık */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl shadow-lg bg-amber-900/40">
              <img src="/assets/gateway.png" alt="gateway" className="w-9 h-9 object-contain" />
            </div>
            <div>
              <Dialog.Title className="text-lg font-semibold tracking-wide text-amber-300">
                {selectedGateway?.label}
              </Dialog.Title>
              <p className="text-xs text-gray-400">{t("mimic.gatewayInfo")}</p>
            </div>
          </div>

          {/* MAC Adresi */}
          <div className="bg-gray-800/60 rounded-lg px-4 py-2 mb-4 flex items-center justify-between">
            <span className="text-xs text-gray-400 font-medium">{t("mimic.macAddress")}</span>
            <span className="text-sm font-mono text-amber-300">{selectedGateway?.mac}</span>
          </div>

          {/* Cihaz Sayıları */}
          <div className="text-xs text-gray-400 mb-4">
            <p className="text-gray-300 font-semibold mb-2 uppercase tracking-wider text-[11px]">
              {t("mimic.connectedDevices")}
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div className="flex justify-between bg-gray-800/40 rounded px-3 py-1.5">
                <span>{t("common.analyzer")}</span>
                <span className="font-bold text-green-400">{selectedGateway?.analyzerCount ?? 0}</span>
              </div>
              <div className="flex justify-between bg-gray-800/40 rounded px-3 py-1.5">
                <span>{t("common.reactive")}</span>
                <span className="font-bold text-blue-400">{selectedGateway?.reactiveCount ?? 0}</span>
              </div>
              <div className="flex justify-between bg-gray-800/40 rounded px-3 py-1.5">
                <span>{t("common.multimeter")}</span>
                <span className="font-bold text-purple-400">{selectedGateway?.multimeterCount ?? 0}</span>
              </div>
              <div className="flex justify-between bg-gray-800/40 rounded px-3 py-1.5 col-span-1">
                <span>{t("common.deviceCount")}</span>
                <span className="font-bold text-amber-300">{selectedGateway?.totalDevices ?? 0}</span>
              </div>
            </div>
          </div>

          {/* Son Haberleşme */}
          {selectedGateway?.last_comm && (() => {
            const lc = formatLastComm(selectedGateway.last_comm);
            return (
              <p className={`text-xs mb-4 ${lc.isLate ? "text-red-400 font-bold" : "text-gray-400"}`}>
                {t("common.lastComm")}: {lc.text}
              </p>
            );
          })()}

          {/* Konum */}
          {selectedGateway?.latitude && selectedGateway?.longitude && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${selectedGateway.latitude},${selectedGateway.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-amber-400 hover:underline flex items-center gap-1 mb-4"
            >
              📍 {selectedGateway.latitude.toFixed(4)}, {selectedGateway.longitude.toFixed(4)}
            </a>
          )}

          {/* Kapat */}
          <button
            onClick={() => setSelectedGateway(null)}
            className="w-full bg-gray-700 text-gray-200 py-2.5 rounded-xl font-medium hover:bg-gray-600 transition-all"
          >
            {t("common.close")}
          </button>
        </Dialog.Panel>
      </Dialog>
    </>
  );
}

export default function MimicDiagram() {
  return (
    <ReactFlowProvider>
      <MimicDiagramInner />
    </ReactFlowProvider>
  );
}
